// ClaimedSubmissionsView.jsx - Display head marker's claimed submissions
import React from 'react';
import { FileText, AlertTriangle, Trash, RotateCcw, Flag, Clock } from 'lucide-react';

function ClaimedSubmissionsView({
  myClaimedSubmissions,
  homeworkNames,
  onStartMarking,
  onUnclaim,
  onForceDelete,
  onReset,
  onFlag
}) {
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      <h2 style={{ margin: '0 0 16px 0', fontSize: '20px' }}>
        My Claimed Submissions
      </h2>
      <p style={{ color: '#7f8c8d', marginBottom: '24px', fontSize: '14px' }}>
        These are submissions you've claimed and are currently marking.
      </p>
      
      {myClaimedSubmissions.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px', 
          color: '#7f8c8d' 
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
            <FileText size={48} color="#bdc3c7" />
          </div>
          <div style={{ fontSize: '18px' }}>No claimed submissions yet</div>
          <div style={{ marginTop: '8px', fontSize: '14px' }}>
            Go to "Available to Mark" tab to claim submissions
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {myClaimedSubmissions.map(submission => (
            <div
              key={submission.id}
              style={{
                border: submission.isLate ? '2px solid #e74c3c' : '2px solid #1b998b',
                borderRadius: '8px',
                padding: '20px',
                background: submission.isLate ? '#fff5f5' : '#f0fdf4'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {submission.studentName}
                    {submission.cohortName && (
                      <span style={{ 
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#667eea',
                        background: '#eef2ff',
                        padding: '4px 12px',
                        borderRadius: '6px'
                      }}>
                        {submission.cohortName}
                      </span>
                    )}
                    {submission.isLate && (
                      <span style={{
                        background: '#e74c3c',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <AlertTriangle size={14} />
                        LATE SUBMISSION
                      </span>
                    )}
                    {submission.examExtraTimeApplied && (
                      <span style={{
                        background: '#eef2ff',
                        color: '#4338ca',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Clock size={14} />
                        EXTRA TIME {submission.examExtraTimeApplied.type === 'percentage'
                          ? `+${submission.examExtraTimeApplied.value}%`
                          : `+${submission.examExtraTimeApplied.value}min`}
                      </span>
                    )}
                    {submission.recoveredFromAbandonedSession && (
                      <span style={{
                        background: '#fef2f2',
                        color: '#b91c1c',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <AlertTriangle size={14} />
                        AUTO-RECOVERED
                      </span>
                    )}
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    fontSize: '14px',
                    color: '#7f8c8d'
                  }}>
                    <span>{homeworkNames[submission.homeworkType] || submission.homeworkType}</span>
                    <span>Submitted {formatDate(submission.submittedAt)}</span>
                    <span>Claimed {formatDate(submission.claimedAt)}</span>
                  </div>
                  
                  {submission.isLate && (
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#e74c3c',
                      fontWeight: '600',
                      marginTop: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <AlertTriangle size={14} />
                      Submitted {Math.floor(submission.minutesLate / 60)}h {submission.minutesLate % 60}m after deadline
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => onStartMarking(submission)}
                    style={{
                      padding: '16px 32px',
                      background: '#c33329',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '16px'
                    }}
                  >
                    Start Marking
                  </button>
                  {onUnclaim && (
                    <button
                      onClick={() => onUnclaim(submission)}
                      style={{
                        padding: '16px 32px',
                        background: 'white',
                        color: '#718096',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '16px'
                      }}
                    >
                      Unclaim
                    </button>
                  )}
                  {onFlag && (
                    <button
                      onClick={() => onFlag(submission)}
                      style={{
                        padding: '16px 24px',
                        background: 'white',
                        color: '#f39c12',
                        border: '2px solid #f39c12',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Flag size={16} />
                      Flag for Review
                    </button>
                  )}
                  {onReset && (
                    <button
                      onClick={() => onReset(submission)}
                      title="Reset this submission so the student can re-upload (use for broken/corrupted files)"
                      style={{
                        padding: '16px',
                        background: 'white',
                        color: '#f39c12',
                        border: '2px solid #f39c12',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <RotateCcw size={18} />
                    </button>
                  )}
                  {onForceDelete && (
                    <button
                      onClick={() => onForceDelete(submission)}
                      title="Permanently delete this submission (use for broken/corrupted submissions)"
                      style={{
                        padding: '16px',
                        background: 'white',
                        color: '#e74c3c',
                        border: '2px solid #e74c3c',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Trash size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ClaimedSubmissionsView;